/**
 * SecretStorageService - Orchestrates Matrix Secret Storage (SSSS) operations
 *
 * Handles:
 * - Opening SSSS with recovery key
 * - Version detection (v1/v2) and KDF handling
 * - Cross-signing secrets management
 * - Megolm backup key retrieval
 * - SSSS bootstrapping for new setups
 */

import { EventEmitter } from 'events';
import matrixService from './matrixService';
import {
  decodeRecoveryKeyString,
  zeroize,
  detectSSSSVersion,
  deriveSSSSKey,
  verifyKeyMAC,
} from '../crypto/utils';
import logger from '../utils/logger';

// Secret names in SSSS
export const SecretNames = {
  CROSS_SIGNING_MASTER: 'm.cross_signing.master',
  CROSS_SIGNING_SELF: 'm.cross_signing.self_signing',
  CROSS_SIGNING_USER: 'm.cross_signing.user_signing',
  MEGOLM_BACKUP_V1: 'm.megolm_backup.v1',
};

// Error codes
export const SSSSErrors = {
  NOT_INITIALIZED: 'SSSS_NOT_INITIALIZED',
  NO_DEFAULT_KEY: 'SSSS_NO_DEFAULT_KEY',
  INVALID_KEY: 'SSSS_INVALID_KEY',
  VERSION_UNSUPPORTED: 'SSSS_VERSION_UNSUPPORTED',
  ALGORITHM_UNSUPPORTED: 'SSSS_ALGORITHM_UNSUPPORTED',
  SECRET_NOT_FOUND: 'SSSS_SECRET_NOT_FOUND',
  DECRYPTION_FAILED: 'SSSS_DECRYPTION_FAILED',
  DERIVATION_FAILED: 'SSSS_DERIVATION_FAILED',
  NETWORK_ERROR: 'SSSS_NETWORK_ERROR',
  UNKNOWN: 'SSSS_UNKNOWN',
};

// Cache configuration
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * SecretStorageService class for managing SSSS operations
 */
class SecretStorageService extends EventEmitter {
  constructor() {
    super();
    this.isOpen = false;
    this.defaultKeyId = null;
    this.keyInfo = null;
    this.cachedKey = null;
    this.cacheExpiry = null;
    this.openSession = null;
  }

  /**
   * Open SSSS with a recovery key
   * @param {string|Uint8Array} recoveryKeyInput - The recovery key string or already decoded bytes
   * @returns {Promise<Object>} Session info with keyId and keyInfo
   */
  async openWithRecoveryKey(recoveryKeyInput) {
    try {
      logger.debug('RECOVERY SecretStorageService: Opening SSSS with recovery key');
      this.emit('state', { state: 'opening' });

      // Get the Matrix client
      const client = matrixService.client;
      if (!client) {
        throw new Error('Matrix client not initialized');
      }

      // Handle both string and Uint8Array inputs
      let keyBytes;
      if (recoveryKeyInput instanceof Uint8Array) {
        // Already decoded bytes
        logger.debug('RECOVERY SecretStorageService: Input is Uint8Array, length:', recoveryKeyInput.length);
        keyBytes = recoveryKeyInput;
      } else if (typeof recoveryKeyInput === 'string') {
        // Decode recovery key string to bytes
        logger.debug('RECOVERY SecretStorageService: Input is string, decoding...');
        try {
          keyBytes = await decodeRecoveryKeyString(recoveryKeyInput);
          logger.debug('RECOVERY SecretStorageService: Decoded to', keyBytes?.length, 'bytes');
        } catch (error) {
          logger.error('RECOVERY SecretStorageService: Failed to decode recovery key:', error.message);
          throw this._createError(SSSSErrors.INVALID_KEY, 'Invalid recovery key format');
        }
      } else {
        logger.error('RECOVERY SecretStorageService: Invalid input type:', typeof recoveryKeyInput);
        throw this._createError(SSSSErrors.INVALID_KEY, 'Recovery key must be a string or Uint8Array');
      }

      // Get default key ID
      const defaultKeyId = await matrixService.getSSSSDefaultKey();
      if (!defaultKeyId) {
        logger.warn('RECOVERY SecretStorageService: No default SSSS key found');
        throw this._createError(SSSSErrors.NO_DEFAULT_KEY, 'No secret storage key configured');
      }

      // Get key info
      const keyInfo = await this.getKeyInfo(defaultKeyId);
      if (!keyInfo) {
        throw this._createError(SSSSErrors.NOT_INITIALIZED, 'Secret storage key info not found');
      }

      // Detect version and validate algorithm
      const versionInfo = detectSSSSVersion(keyInfo);
      if (!versionInfo.supported) {
        throw this._createError(SSSSErrors.VERSION_UNSUPPORTED, `Unsupported SSSS version: ${versionInfo.version}`);
      }
      logger.debug(`RECOVERY SecretStorageService: Detected SSSS ${versionInfo.version}`);
      logger.debug('RECOVERY SecretStorageService: Key info:', {
        algorithm: keyInfo.algorithm,
        hasMAC: !!keyInfo.mac,
        hasIV: !!keyInfo.iv,
        hasPassphrase: !!keyInfo.passphrase,
      });

      // Check if we can use the Rust crypto API
      const crypto = client.getCrypto?.();
      let derivedKey;

      if (crypto?.checkSecretStorageKey) {
        // Use Rust crypto API if available
        logger.debug('RECOVERY SecretStorageService: Using Rust crypto API for SSSS');

        // Check if the key is valid
        logger.debug('RECOVERY SecretStorageService: Checking key validity with Rust API');
        const isValid = await crypto.checkSecretStorageKey(keyBytes, keyInfo);
        logger.debug('RECOVERY SecretStorageService: Rust API validation result:', isValid);
        if (!isValid) {
          throw this._createError(SSSSErrors.INVALID_KEY, 'Recovery key does not match');
        }

        derivedKey = keyBytes; // Rust crypto handles derivation internally
      } else {
        // Fallback to manual derivation
        logger.debug('RECOVERY SecretStorageService: Using manual key derivation');
        derivedKey = await deriveSSSSKey(keyBytes, keyInfo);

        // Validate the derived key
        logger.debug('RECOVERY SecretStorageService: Validating derived key');
        const isValid = await this.checkKeyValidity(defaultKeyId, derivedKey, keyInfo);
        logger.debug('RECOVERY SecretStorageService: Manual validation result:', isValid);
        if (!isValid) {
          throw this._createError(SSSSErrors.INVALID_KEY, 'Recovery key validation failed');
        }
      }

      // Cache the session
      this.isOpen = true;
      this.defaultKeyId = defaultKeyId;
      this.keyInfo = keyInfo;
      this.cachedKey = derivedKey;
      this.cacheExpiry = Date.now() + CACHE_TTL;
      this.openSession = {
        keyId: defaultKeyId,
        keyInfo,
        version: versionInfo.version,
        versionInfo,
        openedAt: Date.now(),
      };

      this.emit('state', { state: 'open', session: this.openSession });
      logger.debug('RECOVERY SecretStorageService: SSSS opened successfully');

      return this.openSession;

    } catch (error) {
      this.emit('error', this._sanitizeError(error));
      this.emit('state', { state: 'error', error: this._sanitizeError(error) });
      throw error;
    }
  }

  /**
   * Get key info for a given key ID
   * @param {string} keyId - The key ID to get info for
   * @returns {Promise<Object|null>} Key info or null
   */
  async getKeyInfo(keyId) {
    try {
      const client = matrixService.client;
      if (!client) {
        throw new Error('Matrix client not initialized');
      }

      // Try to get key info from account data
      const keyInfoEvent = client.getAccountData(`m.secret_storage.key.${keyId}`);
      if (!keyInfoEvent) {
        logger.warn(`SecretStorageService: No key info found for ${keyId}`);
        return null;
      }

      const keyInfo = keyInfoEvent.getContent();
      logger.debug('SecretStorageService: Retrieved key info', {
        keyId,
        algorithm: keyInfo?.algorithm,
        hasPassphrase: !!keyInfo?.passphrase,
      });

      return keyInfo;
    } catch (error) {
      logger.error('SecretStorageService: Error getting key info:', error.message);
      return null;
    }
  }




  /**
   * Check if a key is valid for the given key ID
   * @param {string} keyId - Key ID to check
   * @param {Uint8Array} keyBytes - Key bytes to validate
   * @param {Object} keyInfo - Key info object
   * @returns {Promise<boolean>} True if valid
   */
  async checkKeyValidity(keyId, keyBytes, keyInfo) {
    try {
      const client = matrixService.client;

      // Try Rust crypto API first
      const crypto = client.getCrypto?.();
      if (crypto?.checkSecretStorageKey) {
        logger.debug('RECOVERY checkKeyValidity: Using Rust API');
        const result = await crypto.checkSecretStorageKey(keyBytes, keyInfo);
        logger.debug('RECOVERY checkKeyValidity: Rust API result:', result);
        return result;
      }

      // Fallback: Check MAC if available
      if (keyInfo.mac) {
        logger.debug('RECOVERY checkKeyValidity: Using MAC verification, key length:', keyBytes?.length);
        const result = await verifyKeyMAC(keyBytes, keyInfo);
        logger.debug('RECOVERY checkKeyValidity: MAC verification result:', result);
        return result;
      }

      // If no MAC, try to decrypt a known secret to validate
      // This is less reliable but works as a fallback
      const testSecretName = SecretNames.CROSS_SIGNING_MASTER;
      try {
        await this.getSecret(testSecretName, keyId, keyBytes);
        return true;
      } catch (error) {
        if (error.code === SSSSErrors.SECRET_NOT_FOUND ||
            error.code === SSSSErrors.ALGORITHM_UNSUPPORTED) {
          // Secret doesn't exist or can't decrypt, assume valid if we can't verify
          return true;
        }
        return false;
      }
    } catch (error) {
      logger.error('SecretStorageService: Key validity check failed:', error.message);
      return false;
    }
  }


  /**
   * Get a secret from storage
   * @param {string} secretName - Name of the secret
   * @param {string} keyId - Key ID to use (optional, uses cached if available)
   * @param {Uint8Array} keyBytes - Key bytes (optional, uses cached if available)
   * @returns {Promise<string>} The decrypted secret
   */
  async getSecret(secretName, keyId = null, keyBytes = null) {
    try {
      // Use cached session if available
      if (this.isOpen && this._isCacheValid()) {
        keyId = keyId || this.defaultKeyId;
        keyBytes = keyBytes || this.cachedKey;
      }

      if (!keyId || !keyBytes) {
        throw this._createError(SSSSErrors.NOT_INITIALIZED, 'SSSS not open or key not provided');
      }

      const client = matrixService.client;

      // Use client secret storage (Element Web path)
      const secretStorage = client.getSecretStorage?.();
      if (secretStorage?.get) {
        logger.debug(`SecretStorageService: Getting secret ${secretName} via SecretStorage.get()`);
        const secret = await secretStorage.get(secretName, { keyId });
        if (!secret) {
          throw this._createError(SSSSErrors.SECRET_NOT_FOUND, `Secret ${secretName} not found`);
        }
        return secret;
      }

      // Manual decryption as last resort
      logger.debug('SecretStorageService: Attempting manual secret decryption');
      return await this._manualDecryptSecret(secretName, keyId, keyBytes);

    } catch (error) {
      logger.error(`SecretStorageService: Failed to get secret ${secretName}:`, error.message);
      throw error;
    }
  }

  /**
   * Store a secret in storage
   * @param {string} secretName - Name of the secret
   * @param {string} secretData - Secret data to store
   * @param {string} keyId - Key ID to use (optional, uses default)
   * @returns {Promise<void>}
   */
  async storeSecret(secretName, secretData, keyId = null) {
    try {
      keyId = keyId || this.defaultKeyId;
      if (!keyId) {
        throw this._createError(SSSSErrors.NOT_INITIALIZED, 'No key ID available');
      }

      await matrixService.putSecret(secretName, secretData, keyId);
      logger.debug(`SecretStorageService: Stored secret ${secretName}`);

      this.emit('secret-stored', { name: secretName, keyId });
    } catch (error) {
      logger.error(`SecretStorageService: Failed to store secret ${secretName}:`, error.message);
      throw error;
    }
  }

  /**
   * Check if a secret exists
   * @param {string} secretName - Name of the secret
   * @returns {Promise<boolean>} True if secret exists
   */
  async hasSecret(secretName) {
    try {
      const client = matrixService.client;
      const event = client.getAccountData(secretName);
      return !!event;
    } catch (error) {
      logger.error(`SecretStorageService: Error checking secret ${secretName}:`, error.message);
      return false;
    }
  }

  /**
   * Retrieve all cross-signing secrets
   * @returns {Promise<Object>} Object with master, self_signing, and user_signing keys
   */
  async retrieveCrossSigningSecrets() {
    if (!this.isOpen || !this._isCacheValid()) {
      throw this._createError(SSSSErrors.NOT_INITIALIZED, 'SSSS not open');
    }

    const secrets = {};
    const errors = [];

    // Try to get each cross-signing secret
    for (const [key, secretName] of Object.entries({
      master: SecretNames.CROSS_SIGNING_MASTER,
      self_signing: SecretNames.CROSS_SIGNING_SELF,
      user_signing: SecretNames.CROSS_SIGNING_USER,
    })) {
      try {
        secrets[key] = await this.getSecret(secretName);
        logger.debug(`SecretStorageService: Retrieved ${key} key`);
      } catch (error) {
        if (error.code !== SSSSErrors.SECRET_NOT_FOUND) {
          errors.push({ key, error: error.message });
        }
        logger.warn(`SecretStorageService: Could not retrieve ${key} key:`, error.message);
      }
    }

    if (errors.length > 0 && Object.keys(secrets).length === 0) {
      throw this._createError(SSSSErrors.DECRYPTION_FAILED, 'Failed to retrieve any cross-signing secrets');
    }

    return secrets;
  }

  /**
   * Retrieve megolm backup key
   * @returns {Promise<string|null>} The backup key or null if not found
   */
  async retrieveMegolmBackupKey() {
    if (!this.isOpen || !this._isCacheValid()) {
      throw this._createError(SSSSErrors.NOT_INITIALIZED, 'SSSS not open');
    }

    try {
      const backupKey = await this.getSecret(SecretNames.MEGOLM_BACKUP_V1);
      logger.debug('SecretStorageService: Retrieved megolm backup key');
      return backupKey;
    } catch (error) {
      if (error.code === SSSSErrors.SECRET_NOT_FOUND) {
        logger.debug('SecretStorageService: No megolm backup key in SSSS');
        return null;
      }
      throw error;
    }
  }

  /**
   * Bootstrap SSSS with a new recovery key
   * @param {Uint8Array} recoveryKeyBytes - Recovery key bytes to use
   * @returns {Promise<Object>} Bootstrap result with keyId and keyInfo
   */
  async bootstrapSSSS(recoveryKeyBytes) {
    try {
      this.emit('state', { state: 'bootstrapping' });
      logger.debug('SecretStorageService: Bootstrapping SSSS');

      const client = matrixService.client;
      const crypto = client.getCrypto?.();

      // Use the Rust crypto bootstrapSecretStorage API
      if (crypto?.bootstrapSecretStorage) {
        logger.debug('SecretStorageService: Using Rust crypto bootstrap');

        await crypto.bootstrapSecretStorage({
          createSecretStorageKey: async () => {
            logger.debug('SecretStorageService: Providing recovery key for bootstrap');
            return recoveryKeyBytes;
          },
          setupNewKeyBackup: false, // Don't create new backup
          setupNewSecretStorage: true,
        });

        // Try to get the default key info after bootstrap
        const defaultKeyId = await matrixService.getSSSSDefaultKey();
        if (defaultKeyId) {
          this.defaultKeyId = defaultKeyId;
          this.keyInfo = await this.getKeyInfo(defaultKeyId);
        }

        this.isOpen = true;
        this.cachedKey = recoveryKeyBytes;
        this.cacheExpiry = Date.now() + CACHE_TTL;

        logger.debug('SecretStorageService: SSSS bootstrapped successfully');
        this.emit('state', { state: 'bootstrapped', keyId: this.defaultKeyId });

        return {
          keyId: this.defaultKeyId,
          keyInfo: this.keyInfo,
        };
      }

      // Legacy API fallback
      if (crypto?.createSecretStorageKey) {
        // Use Rust crypto API
        const result = await crypto.createSecretStorageKey({
          key: recoveryKeyBytes,
        });

        this.defaultKeyId = result.keyId;
        this.keyInfo = result.keyInfo;
        this.isOpen = true;
        this.cachedKey = recoveryKeyBytes;
        this.cacheExpiry = Date.now() + CACHE_TTL;

        logger.debug('SecretStorageService: SSSS bootstrapped with legacy API');
        this.emit('state', { state: 'bootstrapped', keyId: result.keyId });
        return result;
      }

      throw new Error('Manual SSSS bootstrap not yet implemented');

    } catch (error) {
      logger.error('SecretStorageService: Bootstrap failed:', error.message);
      this.emit('error', this._sanitizeError(error));
      throw error;
    }
  }

  /**
   * Check if cache is still valid
   * @private
   */
  _isCacheValid() {
    return this.cacheExpiry && Date.now() < this.cacheExpiry;
  }

  /**
   * Manual secret decryption (fallback)
   * @private
   */
  async _manualDecryptSecret(secretName, keyId, keyBytes) {
    try {
      // Get the encrypted data from account data
      const client = matrixService.client;
      const encryptedData = await client.getAccountDataFromServer(secretName);

      if (!encryptedData || !encryptedData.encrypted) {
        throw this._createError(SSSSErrors.SECRET_NOT_FOUND, `Secret ${secretName} not found`);
      }

      // Get the encrypted payload for this key ID
      const encryptedPayload = encryptedData.encrypted[keyId];
      if (!encryptedPayload) {
        throw this._createError(SSSSErrors.SECRET_NOT_FOUND, `Secret ${secretName} not encrypted with key ${keyId}`);
      }

      // Import the deriveKeys function from ssssAlgorithms
      const { deriveKeys } = await import('../crypto/utils/ssssAlgorithms');

      // Derive AES and HMAC keys using HKDF
      logger.debug('RECOVERY SecretStorageService: Deriving keys for decryption');
      const [aesKey, hmacKey] = await deriveKeys(keyBytes, secretName);

      // Decode the ciphertext and MAC from base64
      const ciphertext = Uint8Array.from(atob(encryptedPayload.ciphertext), c => c.charCodeAt(0));
      const mac = Uint8Array.from(atob(encryptedPayload.mac), c => c.charCodeAt(0));
      const iv = Uint8Array.from(atob(encryptedPayload.iv), c => c.charCodeAt(0));

      // Verify MAC
      logger.debug('RECOVERY SecretStorageService: Verifying MAC');
      const isValid = await crypto.subtle.verify(
        { name: 'HMAC' },
        hmacKey,
        mac,
        ciphertext,
      );

      if (!isValid) {
        throw this._createError(SSSSErrors.INVALID_KEY, `MAC verification failed for secret ${secretName}`);
      }

      // Decrypt the ciphertext
      logger.debug('RECOVERY SecretStorageService: Decrypting secret');
      const plaintext = await crypto.subtle.decrypt(
        {
          name: 'AES-CTR',
          counter: iv,
          length: 64, // Counter length in bits
        },
        aesKey,
        ciphertext,
      );

      // Convert to string
      const secret = new TextDecoder().decode(new Uint8Array(plaintext));
      logger.debug(`RECOVERY SecretStorageService: Successfully decrypted secret ${secretName}`);

      return secret;
    } catch (error) {
      logger.error(`RECOVERY SecretStorageService: Manual decryption failed for ${secretName}:`, error.message);
      throw error;
    }
  }

  /**
   * Create an error with code
   * @private
   */
  _createError(code, message) {
    const error = new Error(message);
    error.code = code;
    return error;
  }

  /**
   * Sanitize error for external consumption
   * @private
   */
  _sanitizeError(error) {
    return {
      code: error.code || SSSSErrors.UNKNOWN,
      message: error.message?.replace(/key[:\s]+[\w\s]+/gi, 'key: [REDACTED]') || 'Unknown error',
      name: error.name || 'Error',
    };
  }

  /**
   * Close the SSSS session
   */
  close() {
    if (this.cachedKey) {
      zeroize(this.cachedKey);
    }

    this.isOpen = false;
    this.defaultKeyId = null;
    this.keyInfo = null;
    this.cachedKey = null;
    this.cacheExpiry = null;
    this.openSession = null;

    this.emit('state', { state: 'closed' });
    logger.debug('SecretStorageService: Session closed');
  }

  /**
   * Dispose of the service
   */
  dispose() {
    this.close();
    this.removeAllListeners();
    logger.debug('SecretStorageService: Disposed');
  }
}

// Create singleton instance
const secretStorageService = new SecretStorageService();

export default secretStorageService;
